import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, Input, Typography, Row, Col, Carousel, Tag, Space } from 'antd';
import { RightOutlined } from '@ant-design/icons';

// 导入组件
import SearchBox from '../../components/SearchBox';
import OptimizedImage from '../../components/OptimizedImage';
import { CardSkeleton, BrandSkeleton } from '../../components/Skeleton';

// 导入样式组件
import {
  PageContainer,
  ContentWrapper,
  StyledCard,
  FilterBar,
  FilterRow,
  FilterLabel,
  FilterTag,
  CardSection,
  RecommendGrid,
  RecommendSection,
  BrandSection,
  BrandList,
  AlphaIndex,
  BrandGroupTitle,
  BrandItem,
  CardImage,
  BrandShowcaseSection,
  BrandShowcaseTitle,
  BrandGrid,
  BrandCard,
  BrandLogo,
  BrandName,
  Footer
} from '../../components/Home/styles';

// 导入常量和工具函数
import { filterPrices, filterStyles, allBrands, categoriesTrans, alphabet, levels } from '../../constants';
import { groupBrandsByAlpha, scrollToElement, getCategoryLabel } from '../../utils';
import { getBrandImage } from '../../utils/brandImages';

// 导入自定义 Hook
import { useHomeData } from '../../hooks/useHomeData';

const { Title, Paragraph } = Typography;
const { TabPane } = Tabs;

const Home = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('danban');
  const [brand, setBrand] = useState('');
  const [brandAnchor, setBrandAnchor] = useState('');
  const [price, setPrice] = useState('');
  const [style, setStyle] = useState('');
  const [category, setCategory] = useState('');
  const [level, setLevel] = useState('');
  
  // 使用自定义 Hook 获取数据
  const { 
    categories, 
    recommendedEquipments, 
    loading, 
    categoriesLoading, 
    equipmentsLoading, 
    error
  } = useHomeData();
  const groupedBrands = groupBrandsByAlpha(allBrands);
  const brandListRef = useRef();
  const NAV_HEIGHT = 64; // 顶部导航条高度

  const handleAlphaClick = (letter) => {
    setBrandAnchor(letter);
    const el = document.getElementById('brand-' + letter);
    if (el) {
      if (brandListRef.current && brandListRef.current.scrollHeight > brandListRef.current.clientHeight + 10) {
        // BrandList 区域有滚动条，滚 BrandList
        const parentTop = brandListRef.current.getBoundingClientRect().top;
        const elTop = el.getBoundingClientRect().top;
        const scrollTop = brandListRef.current.scrollTop;
        const offset = elTop - parentTop + scrollTop - NAV_HEIGHT;
        brandListRef.current.scrollTo({
          top: offset,
          behavior: 'smooth'
        });
      } else {
        // fallback: 滚动整个页面
        const elTop = el.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({
          top: elTop - NAV_HEIGHT - 8, // 8px 视觉缓冲
          behavior: 'smooth'
        });
      }
    }
  };

  // 错误状态
  if (error) {
    return <div>加载失败: {error}</div>;
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <SearchBox onFocus={() => navigate('/search')} />

        {/* 分类快捷入口 */}
        <RecommendSection>
          <CardSection style={{ marginBottom: 0 }}>
            {categoriesLoading ? (
              <CardSkeleton count={3} gridMode={true} />
            ) : (
              categories.map((category) => ( 
                <StyledCard
                  key={category.title}
                  hoverable
                  cover={
                    <OptimizedImage
                      alt={category.title}
                      src={
                        category.images && category.images.length > 0
                          ? category.images[Math.floor(Math.random() * category.images.length)]
                          : ''
                      }
                      width="100%"
                      height={160}
                      style={{
                        objectFit: 'contain',
                        padding: '10px',
                        borderRadius: '8px 8px 0 0'
                      }}
                      lazy={false} // 分类图片不使用懒加载，优先显示
                    />
                  }
                  onClick={() => navigate('/products?category=' + category.title)}
                >
                  <StyledCard.Meta 
                    title={category.title} 
                    description={category.subtitle} 
                  />
                </StyledCard>
              ))
            )}
          </CardSection>
        </RecommendSection>

      {/* 推荐装备/猜你喜欢 */}
      <RecommendSection>
        <RecommendGrid>
          {equipmentsLoading ? (
            <CardSkeleton count={10} gridMode={true} />
          ) : (
            recommendedEquipments.filter(item => item !== null).slice(0, 10).map((item) => {
              // images 字段用 | 分割，取第一张
              const firstImage = item.images ? item.images.split('|')[0] : '';
              return (
                <StyledCard
                  key={item.id}
                  hoverable
                  style={{ minWidth: "90px" }}
                  onClick={() => navigate(`/products/${item.id}`)}
                  cover={
                    <div style={{ position: 'relative', height: 160, marginTop: '10px' }}>
                        <OptimizedImage
                          alt={item.name}
                          src={firstImage}
                          width="100%"
                          height="100%"
                          style={{
                            objectFit: item.year === '24/25' ? 'contain' : 'cover'
                          }}
                          lazy={true} // 推荐装备使用懒加载
                        />
                        <Tag
                          color="gold" 
                          style={{ 
                            position: 'absolute', 
                            right: '10px', 
                            bottom: '10px',
                            zIndex: 1
                          }}
                        >
                          {item.year}款
                        </Tag>
                      </div>
                    
                  }
                >
                  <StyledCard.Meta
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: 15 }}>{item.name}</span>
                      </div>
                    }
                    description={
                      <Space direction="vertical" size="small">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          ¥{item.marketPrice}
                        </div>
                        <Space size={4} style={{ flexWrap: 'nowrap', overflow: 'hidden', width: '100%' }}>
                          <Tag color="blue">{item.brandName}</Tag>
                          <Tag color="cyan">{item.flex}</Tag>
                          <Tag color="purple">{getCategoryLabel(item.category, categoriesTrans)}</Tag>
                        </Space>
                      </Space>
                    }
                  />
                </StyledCard>
              );
            })
          )}
        </RecommendGrid>
      </RecommendSection>

      {/* 热门品牌展示 */}
      <BrandShowcaseSection>
        {loading ? (
          <BrandSkeleton count={12} />
        ) : (
          <BrandGrid>
            {['Burton', 'Capita', 'Nitro', 'Flux', 'K2', 'Salomon', 'Jones', 'YES', 'Lib Tech', 'GNU', 'Rome', 'Union'].map((brandName) => (
              <BrandCard
                key={brandName}
                onClick={() => navigate(`/products?brand=${encodeURIComponent(brandName)}`)}
              >
                <OptimizedImage
                  src={getBrandImage(brandName)}
                  alt={brandName}
                  width={80}
                  height={60}
                  style={{
                    objectFit: 'contain',
                    margin: '0 auto 8px'
                  }}
                  lazy={true}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <BrandName>{brandName}</BrandName>
              </BrandCard>
            ))}
          </BrandGrid>
        )}
      </BrandShowcaseSection>

      {/* 筛选区 */}
      <FilterBar>
        <FilterRow>
          <FilterLabel>类型：</FilterLabel>
          {categoriesTrans.map((c) => (
            <FilterTag
              key={c.value}
              checked={category === c.label}
              onChange={() => {
                setCategory(category === c.label ? '' : c.label);
                if (category !== c.label) navigate(`/products?category=${encodeURIComponent(c.label)}`);
              }}
            >
              {c.label}
            </FilterTag>
          ))}
        </FilterRow>
        <FilterRow>
          <FilterLabel>级别：</FilterLabel>
          {levels.map((l) => (
            <FilterTag
              key={l}
              checked={level === l}
              onChange={() => {
                setLevel(level === l ? '' : l);
                if (level !== l) navigate(`/products?level=${encodeURIComponent(l)}`);
              }}
            >
              {l}
            </FilterTag>
          ))}
        </FilterRow>
        <FilterRow>
          <FilterLabel>价格：</FilterLabel>
          {filterPrices.map((p) => (
            <FilterTag
              key={p}
              checked={price === p}
              onChange={() => {
                setPrice(price === p ? '' : p);
                if (price !== p) {
                  // 将价格范围转换为URL参数
                  let minPrice, maxPrice;
                  switch(p) {
                    case '1000以下':
                      minPrice = 0;
                      maxPrice = 999;
                      break;
                    case '1000-1500':
                      minPrice = 1000;
                      maxPrice = 1500;
                      break;
                    case '1500-2500':
                      minPrice = 1500;
                      maxPrice = 2500;
                      break;
                    case '2500以上':
                      minPrice = 2500;
                      maxPrice = 10000;
                      break;
                    default:
                      minPrice = 0;
                      maxPrice = 10000;
                  }
                  navigate(`/products?minPrice=${minPrice}&maxPrice=${maxPrice}`);
                }
              }}
            >
              {p}
            </FilterTag>
          ))}
        </FilterRow>
        <FilterRow>
          <FilterLabel>风格：</FilterLabel>
          {filterStyles.map((s) => (
            <FilterTag
              key={s}
              checked={style === s}
              onChange={() => {
                setStyle(style === s ? '' : s);
                if (style !== s) navigate(`/products?style=${encodeURIComponent(s)}`);
              }}
            >
              {s}
            </FilterTag>
          ))}
        </FilterRow>
      </FilterBar>

      {/* 品牌区块 */}
      <BrandSection>
        <BrandList ref={brandListRef}>
          {alphabet.map(letter =>
            groupedBrands[letter].length > 0 ? (
              <div key={letter} id={'brand-' + letter}>
                <BrandGroupTitle>{letter}</BrandGroupTitle>
                {groupedBrands[letter].map(b => (
                  <BrandItem
                    key={b.brandName}
                    style={{ fontWeight: brand === b.brandName ? 'bold' : undefined }}
                    onClick={() => {
                      setBrand(brand === b.brandName ? '' : b.brandName);
                      if (brand !== b.brandName) navigate(`/products?brand=${encodeURIComponent(b.brandName)}`);
                    }}
                  >
                    <OptimizedImage
                      src={getBrandImage(b.brandName)}
                      alt={b.brandName}
                      width={20}
                      height={20}
                      style={{
                        objectFit: 'contain'
                      }}
                      lazy={true}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {b.brandName}
                  </BrandItem>
                ))}
              </div>
            ) : null
          )}
        </BrandList>
        <AlphaIndex>
          {alphabet.map(letter => (
            <div
              key={letter}
              style={{ 
                color: brandAnchor === letter ? '#1890ff' : '#888', 
                fontWeight: 600, 
                fontSize: 12, 
                padding: '2px 0', 
                cursor: 'pointer' 
              }}
              onClick={() => handleAlphaClick(letter)}
            >
              {letter}
            </div>
          ))}
        </AlphaIndex>
      </BrandSection>
      
      <Footer>
        <div className="beian-info">
          <a href="https://beian.mps.gov.cn/#/query/webSearch" target="_blank" rel="noopener noreferrer">
            <img src="/images/beian-logo.png" alt="备案图标" />
            蜀ICP备2025155379号-1
          </a>
        </div>
        {process.env.REACT_APP_VERSION && (
          <div className="version-info">
            Build v{process.env.REACT_APP_VERSION}
          </div>
        )}
      </Footer>
      </ContentWrapper>
    </PageContainer>
  );
};

export default Home;